import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Splitter } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../LoadingPanel";
import { AdministratorPerformer } from "./Performer";
import { Checkbox, TextBox, InputClearValue } from "@progress/kendo-react-inputs";
import { Pager } from "@progress/kendo-react-data-tools";
import { Icon } from "@progress/kendo-react-common";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { ScreenSize } from '../../../app/ScreenSize';
import { GetScreenSize } from '../../../app/GetScreenSize';
import { getCSRFToken } from '../../../app/getCSRFToken';
import useSpeechToText from 'react-hook-speech-to-text';

import './Performers.scss';

export const AdministratorPerformers = () => {
    const dispatch = useDispatch();

    const [performers, setPerformers] = useState([]);
    const [performersPagerData, setPerformersPagerData] = useState({
        total: 0,
        skip: 0,
        take: 1
    });
    const [selectedPerformerKey, setSelectedPerformerKey] = useState(null);
    const [performer, setPerformer] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [doCreateNewPerformer, setDoCreateNewPerformer] = useState(false);
    const [performerNameFilterValue, setPerformerNameFilterValue] = useState("");
    const [onlyActivePerformersValue, setOnlyActivePerformersValue] = useState(true);
    const [performerLoading, setPerformerLoading] = useState(false);

    const typingTimeout = useRef();
    const typingTimeoutDelay = 500; //Milliseconds

    const {
        error,
        results,
        isRecording,
        startSpeechToText,
        stopSpeechToText,
    } = useSpeechToText({
        timeout: 1000
    });

    const standardPanes = [
        {
            size: "340px",
            min: "5%",
            resizable: false,
        },
        {}
    ];

    const getMobilePanes = () => {
        if (!performer && doCreateNewPerformer === false) {
            return [
                {
                    size: "100%",
                    resizable: false,
                },
                {
                    collapsed: true,
                }
            ];
        }

        return [
            {
                size: "100%",
                resizable: false,
                collapsed: true,
            },
            {
                              
            }
        ];
    }

    const renderPerformerListItem = (props) => {
        let item = props.dataItem;
        
        return (
            <div className={"performerListItem" + (item.performerKey === selectedPerformerKey ? " performerListItemSelected" : "")} onClick={() => selectPerformer(item.performerKey)}>
                <div className="performerListPrimaryText">
                    {item.name}
                </div>
                <div className="performerListSecondaryText">
                    {item.isActive ? "Active" : "Inactive"}
                </div>                
            </div>
        );
    }

    const selectPerformer = (performerKey) => {
        getPerformer(performerKey);
    }

    const createNewPerformer = () => {
        setPerformer(undefined);
        setSelectedPerformerKey(null);
        setPerformerLoading(false);
        setDoCreateNewPerformer(true);

        getPerformers(performerNameFilterValue, onlyActivePerformersValue, performersPagerData.skip, performersPagerData.take);
    }

    const clearPerformer = () => {
        setPerformer(undefined);
        setSelectedPerformerKey(null);
        setPerformerLoading(false);
        setDoCreateNewPerformer(false);

        getPerformers(performerNameFilterValue, onlyActivePerformersValue, performersPagerData.skip, performersPagerData.take);
    }

    const getPerformer = (performerKey) => {
        setSelectedPerformerKey(performerKey);
        setPerformerLoading(true);
        setDoCreateNewPerformer(false);

        const dataItem = {
            performerKey: performerKey,
        };

        fetch(`/api/administrator/getperformer`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setPerformer(result);
                setPerformerLoading(false);

                getPerformers(performerNameFilterValue, onlyActivePerformersValue, performersPagerData.skip, performersPagerData.take);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setPerformer(undefined);
                setPerformerLoading(false);
            });
    }

    const getPerformers = (name, onlyActive, skip, take) => {
        if (!skip) {
            skip = 0;
        }

        if (!take) {
            take = 1;
        }

        const pageNumber = (skip / take) + 1;

        const dataItem = {
            name: name,
            onlyActive: onlyActive,
            pageNumber: pageNumber,
        };

        fetch(`/api/administrator/performers`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setLoading(false);
                setPerformers(result.performers);
                setPerformersPagerData({
                    total: result.totalPerformerCount,
                    skip: (result.pageNumber - 1) * result.performersPerPage,
                    take: result.performersPerPage
                });
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                
                setLoading(false);
                setLoadingError(error.message);
            });        
    }

    const handlePerformerPageChange = (event) => {
        const { skip, take } = event;

        setPerformersPagerData({
            total: performersPagerData.total,
            skip: skip,
            take: take
        });

        getPerformers(performerNameFilterValue, onlyActivePerformersValue, skip, take);
    };

    const performerNameFilterValueChangedEvent = (event) => {
        const value = event.value;
        performerNameFilterValueChanged(value);    
    }

    const performerNameFilterValueChanged = (value) => {
        clearTimeout(typingTimeout.current);
        setPerformerNameFilterValue(value);
        
        typingTimeout.current = setTimeout(() => {
            getPerformers(value, onlyActivePerformersValue);
        }, typingTimeoutDelay);       
    }

    const performerNameClearFilterValue = () => {
        setPerformerNameFilterValue("");
        getPerformers("", onlyActivePerformersValue);
    }

    const onlyActivePerformersValueChanged = (event) => {
        const value = event.value;

        setOnlyActivePerformersValue(value);
        getPerformers(performerNameFilterValue, value);
    }

    useEffect(() => {
        document.title = "Performers";

        getPerformers(performerNameFilterValue, onlyActivePerformersValue);
    }, []);

    useEffect(() => {
        if (!isRecording) {
            if (results.length > 0) {
                const speechValue = results.pop();
                performerNameFilterValueChanged(speechValue); 
            }
        }
    }, [isRecording]);
        
    return (
        <Splitter                
            panes={GetScreenSize() === ScreenSize.Mobile ? getMobilePanes() : standardPanes}
        >                
            <div>
                <div className="performerListFilterHeader">
                    <Button icon="plus" themeColor={"primary"} onClick={createNewPerformer}>Create New Performer</Button>
                </div>
                <div className="performerListFilter">
                    <div className="d-flex flex-row">
                        <TextBox
                            type={"text"}
                            maxLength={"50"}
                            value={performerNameFilterValue}
                            onChange={performerNameFilterValueChangedEvent}
                            placeholder={"Search part of performer name"}   
                            suffix={() => (
                                <>
                                    {performerNameFilterValue !== "" && (
                                        <InputClearValue onClick={performerNameClearFilterValue}>
                                            <Icon name="x" />
                                        </InputClearValue>
                                    )}                                
                                </>
                            )}
                        />

                        {!error && <Button className={isRecording ? "recordButton recording pt-2 pb-0 ms-2" : "recordButton pt-2 pb-0 ms-2"} iconClass="fa-solid fa-microphone" onClick={isRecording ? stopSpeechToText : startSpeechToText} />}
                    </div>
                    <div>
                        <Checkbox
                            label={"Only active"}
                            onChange={onlyActivePerformersValueChanged}
                            value={onlyActivePerformersValue}
                        />
                    </div>
                </div>
                {loading ? <LoadingPanel /> : (loadingError === "" ? (
                    <>
                        <ListView
                            data={performers}
                            item={renderPerformerListItem}
                        />
                        <Pager
                            skip={performersPagerData.skip}
                            take={performersPagerData.take}
                            total={performersPagerData.total}
                            buttonCount={0}
                            info={true}
                            type={"numeric"}
                            previousNext={true}
                            onPageChange={handlePerformerPageChange}
                        />
                    </>) : <div></div>)}
            </div>
            <AdministratorPerformer
                performer={performer}
                loading={performerLoading}
                createNewPerformer={doCreateNewPerformer}
                cancel={clearPerformer}
                selectPerformer={selectPerformer}
            />
        </Splitter>
    );
}